// Interface for payment-provider integration (Flouci or Konnect).
//
// IMPLEMENTATION STATUS: stub. The interface is defined so the UI and
// service layer can be written today; the real Flouci/Konnect HTTP calls
// will be wired in a follow-up sprint.
//
// Why two providers anticipated: Flouci is the broader-coverage Tunisian
// gateway (cards + Edinar + bank transfer), Konnect is a slicker DX. We
// pick one as default but keep the interface neutral.

import type { BillingCycle, PaymentProvider, PlanCode } from './subscription-service';

export interface CheckoutSessionInput {
  /** The plan + cycle the user picked on the billing page. */
  plan: PlanCode;
  cycle: BillingCycle;
  /** Where the gateway should send the user after success/cancel. */
  successUrl: string;
  cancelUrl: string;
  /** The current tenant id — server-side webhook will use this to update
   *  the right tenant_subscriptions row. */
  tenantId: string;
}

export interface CheckoutSession {
  /** URL to redirect the user to so they can pay. */
  redirectUrl: string;
  /** Provider's session/token id, useful for debugging. */
  providerSessionId: string;
  provider: PaymentProvider;
}

export interface IPaymentService {
  /** The provider this implementation is wired against. Allows the UI to
   *  show "Paying with Flouci" without hard-coding it. */
  readonly provider: PaymentProvider;

  /** Open a hosted-checkout session and return the URL to redirect to.
   *  The actual payment + subscription creation happens on the provider's
   *  pages, then a webhook updates `tenant_subscriptions` server-side. */
  createCheckoutSession(input: CheckoutSessionInput): Promise<CheckoutSession>;
}

/** Sentinel thrown by the stub adapter so the UI can show a "coming soon"
 *  state cleanly instead of crashing. */
export class PaymentNotConfiguredError extends Error {
  readonly code = 'payment_not_configured';
  constructor(message = 'No payment provider is configured yet.') {
    super(message);
    this.name = 'PaymentNotConfiguredError';
  }
}
